/**
 * SEO Utilities for szkoleniamorskie.pl
 * Comprehensive SEO helpers for generating metadata, structured data, and canonical URLs
 */

import { Metadata } from 'next';

// Site configuration
export const SITE_CONFIG = {
  name: 'Ośrodek Szkolenia Morskiego LIBRA',
  shortName: 'OSM LIBRA',
  url: 'https://szkoleniamorskie.pl',
  description: 'Profesjonalne szkolenia morskie zgodne z konwencją STCW. Kursy na patenty żeglarskie i motorowodne, szkolenia IMO, certyfikaty morskie. Ponad 15 lat doświadczenia, 98% zdawalność.',
  locale: 'pl_PL',
  language: 'pl',
  // NAP — numery muszą być spójne w CAŁYM repo (config.ts, email-service.ts, rodo-content.ts,
  // sms-service.ts, llms.txt) oraz w Google Business Profile. Wcześniej tu był błędny outlier
  // '+48 94 35 180 98' (nie występował nigdzie indziej) wstrzykiwany do JSON-LD i podawany AI.
  telephone: '+48 94 355 47 45',
  telephoneMobile: '+48 600 909 200',
  email: 'libra@post.pl',
  address: {
    // Ujednolicone na 41 → 37 (2026-08-10): serwis podawał oba numery naraz
    // (constants.ts i podpis mailowy: 37, seo.ts i llms.txt: 41). Niespójny NAP
    // osłabia lokalne SEO i każe modelom AI cytować sprzeczne dane kontaktowe.
    streetAddress: 'ul. Portowa 37',
    addressLocality: 'Kołobrzeg',
    postalCode: '78-100',
    addressCountry: 'PL',
    addressRegion: 'zachodniopomorskie',
  },
  geo: {
    // Współrzędne realnej, zweryfikowanej wizytówki w Mapach Google (CID 0x8b222f6fc9ab5113).
    latitude: 54.1818008,
    longitude: 15.5596155,
  },
  socialMedia: {
    facebook: 'https://www.facebook.com/szkoleniamorskie',
    instagram: 'https://www.instagram.com/szkoleniamorskie',
    // Zweryfikowana wizytówka Google Maps / Google Business Profile (źródło prawdziwych opinii i oceny).
    googleMaps: 'https://www.google.com/maps?cid=10025627877651468563',
  },
  openingHours: 'Mo-Fr 08:00-16:00',
  priceRange: '$$',
  foundingYear: 2008,
};

// Comprehensive keywords database with long-tail keywords for better SEO
export const SEO_KEYWORDS = {
  // Core brand and general keywords
  general: [
    'szkolenia morskie',
    'kursy morskie',
    'certyfikaty morskie',
    'ośrodek szkolenia morskiego',
    'szkolenia dla marynarzy',
    'kursy dla marynarzy',
    'szkolenia morskie Kołobrzeg',
    'kursy morskie Polska',
    'szkolenia żeglarskie',
    'patenty morskie',
    'certyfikaty żeglarskie',
    'uprawnienia morskie',
    'kwalifikacje morskie',
    'szkolenia marityme',
    'maritime training Poland',
  ],
  // STCW certification keywords
  stcw: [
    'STCW',
    'kurs STCW',
    'szkolenie STCW',
    'certyfikat STCW',
    'konwencja STCW',
    'odnowienie STCW',
    'STCW Basic Safety Training',
    'STCW Kołobrzeg',
    'STCW Polska',
    'kursy STCW Manila',
    'szkolenie STCW 2010',
    'certyfikat kompetencji STCW',
    'STCW dla marynarzy',
    'kurs STCW online teoria',
  ],
  // IMO certification keywords
  imo: [
    'IMO',
    'kursy IMO',
    'szkolenia IMO',
    'certyfikat IMO',
    'International Maritime Organization',
    'kursy IMO Polska',
    'szkolenia zgodne z IMO',
    'certyfikaty IMO dla marynarzy',
  ],
  // Motorboat/powerboat keywords
  motorowodne: [
    'patent motorowodny',
    'sternik motorowodny',
    'morski sternik motorowodny',
    'kurs motorowodny',
    'prawo jazdy na łódź',
    'uprawnienia motorowodne',
    'szkolenie motorowodny',
    'egzamin motorowodny',
    'patent motorowodny Kołobrzeg',
    'kurs na patent motorowodny',
    'ile kosztuje patent motorowodny',
    'jak zrobić patent motorowodny',
    'patent na łódź motorową',
    'kurs na skuter wodny',
    'uprawnienia na skuter wodny',
    'patent sternika motorowodnego',
    'morski sternik motorowodny kurs',
    'patent motorowodny cena',
    'patent motorowodny wymagania',
    'patent motorowodny od ilu lat',
  ],
  // Sailing keywords
  zeglarskie: [
    'patent żeglarski',
    'jachtowy sternik morski',
    'kapitan jachtowy',
    'kurs żeglarski',
    'szkolenie żeglarskie',
    'żegluga morska',
    'patent żeglarski morski',
    'kurs na jacht',
    'uprawnienia żeglarskie',
    'sternik jachtowy',
    'kapitan jachtowy kurs',
    'żeglarz jachtowy',
    'patent żeglarza jachtowego',
    'kurs żeglarski morski',
  ],
  // Safety training keywords
  bezpieczenstwo: [
    'bezpieczeństwo morskie',
    'kurs bezpieczeństwa morskiego',
    'szkolenie BHP morskie',
    'bezpieczeństwo na statku',
    'kurs ratownictwa morskiego',
    'ratownictwo morskie szkolenie',
    'survival at sea',
    'kurs przetrwania na morzu',
    'personal survival techniques',
    'PST kurs',
  ],
  // Fire safety keywords
  ppoz: [
    'ochrona przeciwpożarowa',
    'kurs przeciwpożarowy morski',
    'szkolenie ppoż statek',
    'fire prevention and fire fighting',
    'FPFF kurs',
    'gaszenie pożarów na statku',
    'kurs gaśniczy morski',
    'advanced fire fighting',
    'AFF szkolenie',
  ],
  // First aid keywords
  pierwszaPomoc: [
    'pierwsza pomoc morska',
    'kurs pierwszej pomocy morski',
    'medical first aid',
    'MFA kurs',
    'elementary first aid',
    'EFA szkolenie',
    'pierwsza pomoc na statku',
    'medical care on board',
    'kurs medyczny dla marynarzy',
  ],
  // Security keywords
  ochrona: [
    'ochrona statku',
    'ship security officer',
    'SSO kurs',
    'ISPS Code',
    'security awareness',
    'ochrona portowa',
    'PFSO szkolenie',
    'bezpieczeństwo portu',
  ],
  // Location-based keywords (local SEO)
  lokalizacja: [
    'szkolenia morskie Kołobrzeg',
    'kursy morskie zachodniopomorskie',
    'ośrodek szkoleniowy Kołobrzeg',
    'szkolenia STCW Kołobrzeg',
    'kursy motorowodne Kołobrzeg',
    'patenty Kołobrzeg',
    'szkolenia morskie nad morzem',
    'kursy morskie Polska',
    'szkolenia morskie pomorze',
    'kursy żeglarskie Bałtyk',
  ],
  // Career keywords
  kariera: [
    'praca na statku',
    'kariera marynarska',
    'jak zostać marynarzem',
    'praca marynarz',
    'oferty pracy morskie',
    'zatrudnienie na statku',
    'kwalifikacje do pracy na morzu',
    'certyfikaty wymagane na statek',
  ],
};

/**
 * Generate base metadata for any page
 */
export function generateMetadata({
  title,
  description,
  keywords = [],
  path = '',
  image = '/images/og-default.jpg',
  type = 'website',
  noIndex = false,
}: {
  title: string;
  description: string;
  keywords?: string[];
  path?: string;
  image?: string;
  type?: 'website' | 'article';
  noIndex?: boolean;
}): Metadata {
  const url = `${SITE_CONFIG.url}${path}`;
  const fullTitle = title.includes('LIBRA') ? title : `${title} | ${SITE_CONFIG.shortName}`;
  const allKeywords = [...SEO_KEYWORDS.general, ...keywords];

  return {
    // `absolute` omija template z root layout (`%s | OSM LIBRA`) — fullTitle ma już suffix,
    // bez tego powstawał podwójny "... | OSM LIBRA | OSM LIBRA".
    title: { absolute: fullTitle },
    description,
    keywords: allKeywords,
    authors: [{ name: SITE_CONFIG.name }],
    creator: SITE_CONFIG.name,
    publisher: SITE_CONFIG.name,
    metadataBase: new URL(SITE_CONFIG.url),
    alternates: {
      canonical: url,
      languages: {
        'pl-PL': url,
      },
    },
    openGraph: {
      title: fullTitle,
      description,
      url,
      siteName: SITE_CONFIG.name,
      locale: SITE_CONFIG.locale,
      type,
      images: [
        {
          url: image.startsWith('http') ? image : `${SITE_CONFIG.url}${image}`,
          width: 1200,
          height: 630,
          alt: title,
        },
      ],
    },
    twitter: {
      card: 'summary_large_image',
      title: fullTitle,
      description,
      images: [image.startsWith('http') ? image : `${SITE_CONFIG.url}${image}`],
    },
    robots: noIndex
      ? { index: false, follow: true } // noindex, follow — wypadnij z indeksu, ale podążaj za linkami (equity)
      : {
          index: true,
          follow: true,
          googleBot: {
            index: true,
            follow: true,
            'max-video-preview': -1,
            'max-image-preview': 'large',
            'max-snippet': -1,
          },
        },
    // Kod weryfikacyjny GSC z env (GOOGLE_SITE_VERIFICATION).
    // Gdy brak — nie renderujemy meta tagu (lepiej brak niż fałszywy placeholder).
    verification: process.env.GOOGLE_SITE_VERIFICATION
      ? { google: process.env.GOOGLE_SITE_VERIFICATION }
      : undefined,
  };
}

/**
 * Organization Schema (JSON-LD)
 */
export function generateOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    '@id': `${SITE_CONFIG.url}/#organization`,
    name: SITE_CONFIG.name,
    alternateName: SITE_CONFIG.shortName,
    url: SITE_CONFIG.url,
    logo: {
      '@type': 'ImageObject',
      url: `${SITE_CONFIG.url}/images/logo.webp`,
      width: 300,
      height: 100,
    },
    image: `${SITE_CONFIG.url}/images/og-default.jpg`,
    description: SITE_CONFIG.description,
    telephone: SITE_CONFIG.telephone,
    email: SITE_CONFIG.email,
    address: {
      '@type': 'PostalAddress',
      ...SITE_CONFIG.address,
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: SITE_CONFIG.geo.latitude,
      longitude: SITE_CONFIG.geo.longitude,
    },
    foundingDate: SITE_CONFIG.foundingYear.toString(),
    slogan: 'Twoja droga na morze zaczyna się tutaj',
    areaServed: {
      '@type': 'Country',
      name: 'Poland',
    },
    sameAs: [
      SITE_CONFIG.socialMedia.facebook,
      SITE_CONFIG.socialMedia.instagram,
      SITE_CONFIG.socialMedia.googleMaps,
    ],
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: SITE_CONFIG.telephone,
        contactType: 'customer service',
        availableLanguage: ['Polish', 'English'],
        areaServed: 'PL',
      },
      {
        '@type': 'ContactPoint',
        telephone: SITE_CONFIG.telephoneMobile,
        contactType: 'customer service',
        availableLanguage: ['Polish', 'English'],
        areaServed: 'PL',
      },
    ],
    hasCredential: [
      {
        '@type': 'EducationalOccupationalCredential',
        name: 'Certyfikat ISO 9001',
        credentialCategory: 'Quality Management',
        recognizedBy: {
          '@type': 'Organization',
          name: 'Biuro Certyfikacji Polskiego Rejestru Statków S.A.',
        },
      },
      {
        '@type': 'EducationalOccupationalCredential',
        name: 'Uznanie Ministerstwa Infrastruktury',
        credentialCategory: 'Government Accreditation',
      },
    ],
  };
}

/**
 * LocalBusiness Schema (JSON-LD)
 */
export function generateLocalBusinessSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${SITE_CONFIG.url}/#localbusiness`,
    name: SITE_CONFIG.name,
    image: `${SITE_CONFIG.url}/images/og-default.jpg`,
    url: SITE_CONFIG.url,
    hasMap: SITE_CONFIG.socialMedia.googleMaps,
    telephone: SITE_CONFIG.telephone,
    email: SITE_CONFIG.email,
    priceRange: SITE_CONFIG.priceRange,
    address: {
      '@type': 'PostalAddress',
      ...SITE_CONFIG.address,
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: SITE_CONFIG.geo.latitude,
      longitude: SITE_CONFIG.geo.longitude,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '08:00',
        closes: '16:00',
      },
    ],
    sameAs: [
      SITE_CONFIG.socialMedia.facebook,
      SITE_CONFIG.socialMedia.instagram,
      SITE_CONFIG.socialMedia.googleMaps,
    ],
    // ŚWIADOMIE BEZ `aggregateRating`: Google nie pokazuje gwiazdek dla „self-serving" oceny o WŁASNEJ
    // firmie na własnej stronie i zakazuje agregowania opinii z innego źródła (Mapy Google = inne źródło).
    // Prawdziwa ocena (4.9/56) jest połączona z encją przez `sameAs` + `hasMap` → wizytówka GBP. Google
    // pokazuje gwiazdki w Mapach/Knowledge Panel, a AI może je stamtąd zacytować. To bezpieczna, zgodna droga.
  };
}

/**
 * WebSite Schema with SearchAction (JSON-LD)
 */
export function generateWebsiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_CONFIG.url}/#website`,
    url: SITE_CONFIG.url,
    name: SITE_CONFIG.name,
    description: SITE_CONFIG.description,
    publisher: {
      '@id': `${SITE_CONFIG.url}/#organization`,
    },
    inLanguage: SITE_CONFIG.language,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_CONFIG.url}/training?search={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

/**
 * Wyciąga bazową kwotę kursu (pierwszą liczbę) z opisowej kolumny `cena`.
 * Format danych jest spójny: "Cena 1530 PLN + 20 PLN dla Urzędu Morskiego za certyfikat".
 *
 * STARY BUG: price.replace(/[^0-9]/g,'') usuwał WSZYSTKIE nie-cyfry i sklejał kwoty,
 * np. "Cena 1530 PLN + 20 PLN" → "153020" (cena bazowa zawyżona ~100×, wysyłana do Google/AI).
 * Teraz bierzemy pierwszą kwotę = bazową cenę kursu (opłaty urzędowe to osobny koszt, nie cena oferty).
 */
export function extractPriceAmount(price?: string): string | undefined {
  if (!price) return undefined;
  // Pierwszy ciąg cyfr (z opcjonalnymi separatorami tysięcy: spacja / nbsp / kropka).
  const match = String(price).match(/\d[\d\s .]*/);
  if (!match) return undefined;
  const digits = match[0].replace(/[^\d]/g, '');
  return digits.length > 0 ? digits : undefined;
}

/**
 * Course Schema (JSON-LD)
 */
export function generateCourseSchema({
  name,
  description,
  provider = SITE_CONFIG.name,
  url,
  image,
  price,
  duration,
  courseCode,
  educationalLevel,
  teaches,
  numberOfCredits,
  credentialAwarded,
  dateModified,
  courseInstances,
}: {
  name: string;
  description: string;
  provider?: string;
  url: string;
  image?: string;
  price?: string;
  duration?: string;
  courseCode?: string;
  educationalLevel?: string;
  teaches?: string[];
  numberOfCredits?: string;
  credentialAwarded?: string;
  dateModified?: string;
  courseInstances?: Array<{
    startDate: string;
    endDate?: string;
    location?: string;
    price?: string;
    url?: string;
  }>;
}) {
  const absoluteUrl = url.startsWith('http') ? url : `${SITE_CONFIG.url}${url}`;
  const priceAmount = extractPriceAmount(price);

  // Realne terminy z tabeli `nadchodzace` → tablica CourseInstance ze startDate/endDate/location/offers.
  // To one dają „żywy" Course Info w Google i twarde, cytowalne fakty dla LLM (kiedy, gdzie, za ile).
  const realInstances = (courseInstances || [])
    .filter((ci) => ci.startDate)
    .map((ci) => {
      const ciPrice = extractPriceAmount(ci.price ?? price);
      return {
        '@type': 'CourseInstance',
        courseMode: 'onsite',
        ...(duration && { courseWorkload: duration }),
        inLanguage: 'pl',
        startDate: ci.startDate,
        ...(ci.endDate && { endDate: ci.endDate }),
        location: {
          '@type': 'Place',
          name: `${SITE_CONFIG.name}${ci.location ? ` — ${ci.location}` : ''}`,
          address: {
            '@type': 'PostalAddress',
            ...SITE_CONFIG.address,
          },
        },
        ...(ciPrice && {
          offers: {
            '@type': 'Offer',
            price: ciPrice,
            priceCurrency: 'PLN',
            availability: 'https://schema.org/InStock',
            url: ci.url
              ? (ci.url.startsWith('http') ? ci.url : `${SITE_CONFIG.url}${ci.url}`)
              : absoluteUrl,
          },
        }),
        instructor: {
          '@type': 'Person',
          name: 'Instruktor OSM LIBRA',
        },
      };
    });

  // Fallback: brak realnych terminów → pojedyncza instancja oparta o czas trwania.
  const fallbackInstance = duration
    ? [
        {
          '@type': 'CourseInstance',
          courseMode: 'onsite',
          courseWorkload: duration,
          inLanguage: 'pl',
          instructor: { '@type': 'Person', name: 'Instruktor OSM LIBRA' },
        },
      ]
    : [];

  const instances = realInstances.length > 0 ? realInstances : fallbackInstance;

  return {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name,
    description,
    inLanguage: 'pl',
    provider: {
      '@type': 'EducationalOrganization',
      '@id': `${SITE_CONFIG.url}/#organization`,
      name: provider,
      url: SITE_CONFIG.url,
    },
    url: absoluteUrl,
    image: image ? (image.startsWith('http') ? image : `${SITE_CONFIG.url}${image}`) : undefined,
    ...(courseCode && { courseCode }),
    ...(educationalLevel && { educationalLevel }),
    ...(teaches && { teaches }),
    ...(numberOfCredits && { numberOfCredits }),
    ...(credentialAwarded && { educationalCredentialAwarded: credentialAwarded }),
    ...(dateModified && { dateModified }),
    ...(duration && { timeRequired: duration }),
    ...(instances.length > 0 && { hasCourseInstance: instances }),
    ...(priceAmount && {
      offers: {
        '@type': 'Offer',
        price: priceAmount,
        priceCurrency: 'PLN',
        availability: 'https://schema.org/InStock',
        url: absoluteUrl,
        validFrom: new Date().toISOString().split('T')[0],
      },
    }),
  };
}

/**
 * Event Schema for course instances (JSON-LD)
 */
export function generateEventSchema({
  name,
  description,
  startDate,
  endDate,
  location = SITE_CONFIG.address.addressLocality,
  url,
  image,
  price,
  organizer = SITE_CONFIG.name,
  availableSeats,
}: {
  name: string;
  description: string;
  startDate: string;
  endDate?: string;
  location?: string;
  url: string;
  image?: string;
  price?: string;
  organizer?: string;
  availableSeats?: number;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'EducationEvent',
    name,
    description,
    startDate,
    endDate: endDate || startDate,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: {
      '@type': 'Place',
      name: `${SITE_CONFIG.name} - ${location}`,
      address: {
        '@type': 'PostalAddress',
        ...SITE_CONFIG.address,
        addressLocality: location,
      },
    },
    organizer: {
      '@type': 'Organization',
      name: organizer,
      url: SITE_CONFIG.url,
    },
    url: url.startsWith('http') ? url : `${SITE_CONFIG.url}${url}`,
    image: image ? (image.startsWith('http') ? image : `${SITE_CONFIG.url}${image}`) : undefined,
    ...(price && {
      offers: {
        '@type': 'Offer',
        price: extractPriceAmount(price),
        priceCurrency: 'PLN',
        availability: availableSeats && availableSeats > 0
          ? 'https://schema.org/InStock'
          : 'https://schema.org/SoldOut',
        url: url.startsWith('http') ? url : `${SITE_CONFIG.url}${url}`,
        validFrom: new Date().toISOString().split('T')[0],
      },
    }),
    ...(availableSeats !== undefined && {
      maximumAttendeeCapacity: availableSeats,
      remainingAttendeeCapacity: availableSeats,
    }),
  };
}

/**
 * Harmonogram szkoleń (JSON-LD) — ItemList z EducationEvent dla KAŻDEGO nadchodzącego terminu.
 * Niewidoczny dla użytkownika (tag <script>), ale daje Google i LLM-om kompletny, maszynowo-czytelny
 * harmonogram: który kurs, dokładna data od–do, lokalizacja, cena, dostępność miejsc.
 */
export function generateCourseScheduleSchema(
  events: Array<{
    title: string;
    startDate: string;
    endDate?: string;
    price?: string;
    url: string;
    hasSeats?: boolean;
  }>
) {
  const items = events
    .filter((ev) => ev.title && ev.startDate)
    .map((ev, i) => {
      const absoluteUrl = ev.url.startsWith('http') ? ev.url : `${SITE_CONFIG.url}${ev.url}`;
      const priceAmount = extractPriceAmount(ev.price);
      return {
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'EducationEvent',
          name: ev.title,
          startDate: ev.startDate,
          ...(ev.endDate && { endDate: ev.endDate }),
          eventStatus: 'https://schema.org/EventScheduled',
          eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
          inLanguage: 'pl',
          location: {
            '@type': 'Place',
            name: SITE_CONFIG.name,
            address: {
              '@type': 'PostalAddress',
              ...SITE_CONFIG.address,
            },
          },
          organizer: {
            '@type': 'Organization',
            '@id': `${SITE_CONFIG.url}/#organization`,
            name: SITE_CONFIG.name,
            url: SITE_CONFIG.url,
          },
          ...(priceAmount && {
            offers: {
              '@type': 'Offer',
              price: priceAmount,
              priceCurrency: 'PLN',
              availability:
                ev.hasSeats === false
                  ? 'https://schema.org/SoldOut'
                  : 'https://schema.org/InStock',
              url: absoluteUrl,
            },
          }),
          url: absoluteUrl,
        },
      };
    });

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Harmonogram nadchodzących szkoleń morskich — Ośrodek Szkolenia Morskiego LIBRA, Kołobrzeg',
    description:
      'Aktualne terminy kursów STCW, szkoleń bezpieczeństwa morskiego oraz kursów i patentów motorowodnych w OSM LIBRA w Kołobrzegu.',
    numberOfItems: items.length,
    itemListOrder: 'https://schema.org/ItemListOrderAscending',
    itemListElement: items,
  };
}

/**
 * FAQ Schema (JSON-LD)
 */
export function generateFAQSchema(faqs: Array<{ question: string; answer: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

/**
 * BreadcrumbList Schema (JSON-LD)
 */
export function generateBreadcrumbSchema(
  items: Array<{ name: string; url: string }>
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url.startsWith('http') ? item.url : `${SITE_CONFIG.url}${item.url}`,
    })),
  };
}

/**
 * Service Schema (JSON-LD)
 */
export function generateServiceSchema({
  name,
  description,
  url,
  price,
  areaServed = 'Poland',
}: {
  name: string;
  description: string;
  url: string;
  price?: string;
  areaServed?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Maritime Training',
    name,
    description,
    provider: {
      '@type': 'EducationalOrganization',
      name: SITE_CONFIG.name,
      url: SITE_CONFIG.url,
    },
    areaServed: {
      '@type': 'Country',
      name: areaServed,
    },
    url: url.startsWith('http') ? url : `${SITE_CONFIG.url}${url}`,
    ...(price && {
      offers: {
        '@type': 'Offer',
        price: extractPriceAmount(price),
        priceCurrency: 'PLN',
      },
    }),
  };
}

/**
 * Combine multiple schemas into a single array for injection
 */
export function combineSchemas(...schemas: object[]) {
  return schemas;
}

/**
 * Generate script tag content for JSON-LD
 */
export function schemaToScript(schema: object | object[]): string {
  return JSON.stringify(Array.isArray(schema) ? schema : [schema]);
}

/**
 * Professional Service Schema for maritime training services
 */
export function generateProfessionalServiceSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    '@id': `${SITE_CONFIG.url}/#professionalservice`,
    name: SITE_CONFIG.name,
    alternateName: [
      'OSM LIBRA',
      'Szkolenia Morskie LIBRA',
      'LIBRA Maritime Training Center',
    ],
    url: SITE_CONFIG.url,
    logo: `${SITE_CONFIG.url}/images/logo.webp`,
    image: `${SITE_CONFIG.url}/images/og-default.jpg`,
    description: SITE_CONFIG.description,
    telephone: SITE_CONFIG.telephone,
    email: SITE_CONFIG.email,
    address: {
      '@type': 'PostalAddress',
      ...SITE_CONFIG.address,
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: SITE_CONFIG.geo.latitude,
      longitude: SITE_CONFIG.geo.longitude,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '08:00',
        closes: '16:00',
      },
    ],
    priceRange: SITE_CONFIG.priceRange,
    currenciesAccepted: 'PLN',
    paymentAccepted: 'Cash, Credit Card, Bank Transfer',
    areaServed: [
      { '@type': 'Country', name: 'Poland' },
      { '@type': 'AdministrativeArea', name: 'Zachodniopomorskie' },
    ],
    serviceType: [
      'Maritime Training',
      'STCW Certification',
      'Sailing Courses',
      'Powerboat Licensing',
      'Marine Safety Training',
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Katalog Szkoleń Morskich',
      itemListElement: [
        {
          '@type': 'OfferCatalog',
          name: 'Kursy STCW',
          itemListElement: [
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'STCW Basic Safety Training',
                description: 'Podstawowe szkolenie w zakresie bezpieczeństwa zgodnie z konwencją STCW',
              },
            },
          ],
        },
        {
          '@type': 'OfferCatalog',
          name: 'Patenty Motorowodne',
          itemListElement: [
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Patent Sternika Motorowodnego',
                description: 'Kurs na patent sternika motorowodnego uprawniający do prowadzenia łodzi motorowych',
              },
            },
          ],
        },
        {
          '@type': 'OfferCatalog',
          name: 'Patenty Żeglarskie',
          itemListElement: [
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Jachtowy Sternik Morski',
                description: 'Kurs na patent jachtowego sternika morskiego',
              },
            },
          ],
        },
      ],
    },
    knowsAbout: [
      'STCW Convention',
      'STCW Basic Safety Training',
      'Personal Survival Techniques',
      'Proficiency in Survival Craft and Rescue Boats',
      'Fire Prevention and Fire Fighting',
      'Elementary First Aid',
      'Medical First Aid',
      'Security Awareness',
      'Crowd Management',
      'STCW Refresher Training',
      'IMO Regulations',
      'Maritime Safety',
      'Fire Fighting at Sea',
      'Sea Survival',
      'First Aid at Sea',
      'Ship Security',
      'Powerboat Navigation',
      'Powerboat Licence',
      'Sailing',
    ],
    sameAs: [
      SITE_CONFIG.socialMedia.facebook,
      SITE_CONFIG.socialMedia.instagram,
      SITE_CONFIG.socialMedia.googleMaps,
    ],
  };
}

/**
 * ItemList Schema for course catalog
 */
export function generateItemListSchema(items: Array<{
  name: string;
  description: string;
  url: string;
  image?: string;
  price?: string;
}>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Course',
        name: item.name,
        description: item.description,
        url: item.url.startsWith('http') ? item.url : `${SITE_CONFIG.url}${item.url}`,
        ...(item.image && {
          image: item.image.startsWith('http') ? item.image : `${SITE_CONFIG.url}${item.image}`,
        }),
        provider: {
          '@type': 'EducationalOrganization',
          name: SITE_CONFIG.name,
          url: SITE_CONFIG.url,
        },
        ...(item.price && {
          offers: {
            '@type': 'Offer',
            price: item.price.replace(/[^0-9]/g, ''),
            priceCurrency: 'PLN',
            availability: 'https://schema.org/InStock',
          },
        }),
      },
    })),
  };
}

/**
 * Review Schema for testimonials
 */
export function generateReviewSchema(reviews: Array<{
  author: string;
  rating: number;
  text: string;
  date?: string;
}>) {
  return reviews.map((review) => ({
    '@context': 'https://schema.org',
    '@type': 'Review',
    itemReviewed: {
      '@type': 'EducationalOrganization',
      name: SITE_CONFIG.name,
      url: SITE_CONFIG.url,
    },
    author: {
      '@type': 'Person',
      name: review.author,
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: review.rating,
      bestRating: 5,
      worstRating: 1,
    },
    reviewBody: review.text,
    ...(review.date && { datePublished: review.date }),
  }));
}

/**
 * How-To Schema for training process
 */
export function generateHowToSchema({
  name,
  description,
  steps,
  totalTime,
  estimatedCost,
}: {
  name: string;
  description: string;
  steps: Array<{ name: string; text: string; url?: string; image?: string }>;
  totalTime?: string;
  estimatedCost?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    description,
    ...(totalTime && { totalTime }),
    ...(estimatedCost && {
      estimatedCost: {
        '@type': 'MonetaryAmount',
        currency: 'PLN',
        value: estimatedCost.replace(/[^0-9]/g, ''),
      },
    }),
    step: steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
      ...(step.url && { url: step.url.startsWith('http') ? step.url : `${SITE_CONFIG.url}${step.url}` }),
      ...(step.image && { image: step.image.startsWith('http') ? step.image : `${SITE_CONFIG.url}${step.image}` }),
    })),
  };
}

/**
 * Generate all homepage schemas combined
 */
export function generateHomepageSchemas() {
  return [
    generateOrganizationSchema(),
    generateLocalBusinessSchema(),
    generateWebsiteSchema(),
    generateProfessionalServiceSchema(),
  ];
}

/**
 * ContactPage Schema
 */
export function generateContactPageSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Kontakt - Ośrodek Szkolenia Morskiego LIBRA',
    description: 'Skontaktuj się z nami w sprawie szkoleń morskich, certyfikatów STCW i patentów motorowodnych.',
    url: `${SITE_CONFIG.url}/contact`,
    mainEntity: {
      '@type': 'Organization',
      name: SITE_CONFIG.name,
      telephone: SITE_CONFIG.telephone,
      email: SITE_CONFIG.email,
      address: {
        '@type': 'PostalAddress',
        ...SITE_CONFIG.address,
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: SITE_CONFIG.telephone,
        contactType: 'customer service',
        availableLanguage: ['Polish', 'English'],
        areaServed: 'PL',
        hoursAvailable: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '08:00',
          closes: '16:00',
        },
      },
    },
  };
}

/**
 * AboutPage Schema
 */
export function generateAboutPageSchema() {
  const yearsExperience = new Date().getFullYear() - SITE_CONFIG.foundingYear;
  return {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'O nas — Ośrodek Szkolenia Morskiego LIBRA',
    description:
      `Ośrodek Szkolenia Morskiego LIBRA to ośrodek szkoleń morskich w Kołobrzegu (województwo zachodniopomorskie, wybrzeże Morza Bałtyckiego) z ponad ${yearsExperience}-letnim doświadczeniem. ` +
      'Prowadzimy kursy STCW i szkolenia bezpieczeństwa morskiego dla marynarzy, szkolenia specjalistyczne i radiowe oraz kursy i patenty motorowodne i żeglarskie. ' +
      'Na podstawie odbytych przeszkoleń Polska Administracja Morska wystawia międzynarodowe dokumenty uprawniające do pracy na statkach w kraju i na świecie. ' +
      'Ośrodek posiada Uznanie Ministerstwa Infrastruktury, Certyfikat ISO 9001 (zatwierdzony przez Biuro Certyfikacji Polskiego Rejestru Statków S.A.) oraz działa zgodnie z Konwencją STCW 78/95. ' +
      'Współpracujemy z Organizacją Marynarzy Kontraktowych NSZZ „Solidarność".',
    url: `${SITE_CONFIG.url}/about`,
    inLanguage: 'pl',
    mainEntity: generateOrganizationSchema(),
  };
}

/**
 * CollectionPage Schema for training catalog
 */
export function generateTrainingCatalogSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Katalog Szkoleń Morskich - LIBRA',
    description: 'Pełen katalog szkoleń morskich: kursy STCW, patenty motorowodne, żeglarskie i certyfikaty IMO.',
    url: `${SITE_CONFIG.url}/training`,
    isPartOf: {
      '@type': 'WebSite',
      '@id': `${SITE_CONFIG.url}/#website`,
    },
    about: {
      '@type': 'Thing',
      name: 'Szkolenia Morskie',
      description: 'Profesjonalne szkolenia morskie zgodne z międzynarodowymi standardami STCW i IMO.',
    },
    provider: {
      '@type': 'EducationalOrganization',
      name: SITE_CONFIG.name,
      url: SITE_CONFIG.url,
    },
  };
}

/**
 * Video Schema for promotional content
 */
export function generateVideoSchema({
  name,
  description,
  thumbnailUrl,
  uploadDate,
  duration,
  contentUrl,
}: {
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  duration?: string;
  contentUrl?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name,
    description,
    thumbnailUrl: thumbnailUrl.startsWith('http') ? thumbnailUrl : `${SITE_CONFIG.url}${thumbnailUrl}`,
    uploadDate,
    ...(duration && { duration }),
    ...(contentUrl && { contentUrl: contentUrl.startsWith('http') ? contentUrl : `${SITE_CONFIG.url}${contentUrl}` }),
    publisher: {
      '@type': 'Organization',
      name: SITE_CONFIG.name,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_CONFIG.url}/images/logo.webp`,
      },
    },
  };
}

/**
 * Speakable Schema for voice search optimization
 */
export function generateSpeakableSchema(cssSelectors: string[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: cssSelectors,
    },
  };
}

/**
 * Generate SEO-optimized page title
 */
export function generatePageTitle(title: string, includeLocation = true): string {
  const brandSuffix = includeLocation
    ? `${SITE_CONFIG.shortName} Kołobrzeg`
    : SITE_CONFIG.shortName;

  if (title.toLowerCase().includes('libra')) {
    return title;
  }

  return `${title} | ${brandSuffix}`;
}

/**
 * Generate SEO-optimized meta description
 */
export function generateMetaDescription(description: string, maxLength = 160): string {
  if (description.length <= maxLength) {
    return description;
  }

  // Find last complete sentence within limit
  const truncated = description.substring(0, maxLength);
  const lastPeriod = truncated.lastIndexOf('.');
  const lastQuestion = truncated.lastIndexOf('?');
  const lastExclaim = truncated.lastIndexOf('!');

  const lastSentenceEnd = Math.max(lastPeriod, lastQuestion, lastExclaim);

  if (lastSentenceEnd > maxLength * 0.6) {
    return description.substring(0, lastSentenceEnd + 1);
  }

  // Otherwise, find last complete word
  const lastSpace = truncated.lastIndexOf(' ');
  return description.substring(0, lastSpace) + '...';
}

/**
 * Pre-defined SEO configurations for key pages
 */
export const PAGE_SEO_CONFIG = {
  home: {
    title: 'Szkolenia Morskie STCW i Patenty | Ośrodek Szkolenia Morskiego LIBRA Kołobrzeg',
    description: 'Profesjonalne szkolenia morskie STCW, patenty motorowodne i żeglarskie w Kołobrzegu. 98% zdawalność, 15+ lat doświadczenia, certyfikaty uznawane na całym świecie. Zapisz się!',
    keywords: [...SEO_KEYWORDS.general, ...SEO_KEYWORDS.stcw, ...SEO_KEYWORDS.lokalizacja],
  },
  training: {
    title: 'Katalog Szkoleń Morskich | Kursy STCW, IMO, Patenty',
    description: 'Pełen katalog szkoleń morskich: kursy STCW, certyfikaty IMO, patenty motorowodne i żeglarskie. Sprawdź terminy i ceny. Zapisz się online na szkolenie w OSM LIBRA Kołobrzeg.',
    keywords: [...SEO_KEYWORDS.general, ...SEO_KEYWORDS.stcw, ...SEO_KEYWORDS.imo],
  },
  motorowodne: {
    title: 'Patent Motorowodny | Kurs Sternika Motorowodnego Kołobrzeg',
    description: 'Zdobądź patent motorowodny w OSM LIBRA! Kurs sternika motorowodnego i morskiego sternika motorowodnego. Teoria + praktyka na wodzie. Wysoka zdawalność egzaminów. Zapisz się!',
    keywords: [...SEO_KEYWORDS.motorowodne, ...SEO_KEYWORDS.lokalizacja],
  },
  contact: {
    title: 'Kontakt | Ośrodek Szkolenia Morskiego LIBRA Kołobrzeg',
    description: 'Skontaktuj się z OSM LIBRA w Kołobrzegu. Telefon, email, adres i godziny otwarcia. Zapisz się na szkolenie morskie lub zadaj pytanie o kursy STCW i patenty.',
    keywords: [...SEO_KEYWORDS.lokalizacja, 'kontakt szkolenia morskie', 'zapisz się na kurs morski'],
  },
  about: {
    title: 'O Nas | Historia i Misja OSM LIBRA',
    description: 'Poznaj Ośrodek Szkolenia Morskiego LIBRA. 15+ lat doświadczenia, wykwalifikowani instruktorzy, certyfikaty uznawane międzynarodowo. Nasza historia i misja.',
    keywords: [...SEO_KEYWORDS.general, 'o nas ośrodek szkolenia morskiego', 'historia OSM LIBRA'],
  },
};
